import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from '../hooks/use-i18n'
import { isAdminUser } from '../utils/admin'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { Link } from 'react-router-dom'
import { ArrowLeft, RefreshCw } from 'lucide-react'
import agentService, { AgentAnalyticsDashboard } from '../services/agentService'

const AdminAgentAnalytics: React.FC = () => {
  const { t } = useTranslation()
  const { user } = useSelector((state: RootState) => state.auth)
  const [data, setData] = useState<AgentAnalyticsDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await agentService.getAdminAnalytics())
    } catch {
      setError(t('agent.adminAnalyticsError'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    if (isAdminUser(user)) void load()
  }, [user, load])

  if (!user || !isAdminUser(user)) return null

  return (
    <div className="min-h-screen bg-background pt-16 pb-8">
      <div className="container mx-auto px-4 max-w-3xl">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft size={16} /> {t('dashboard.backToDashboard')}
        </Link>
        <div className="flex items-center justify-between gap-3 mb-6">
          <h1 className="text-lg font-semibold text-foreground">{t('agent.adminAnalyticsTitle')}</h1>
          <button type="button" onClick={() => void load()} className="inline-flex items-center gap-2 text-sm text-primary">
            <RefreshCw size={16} /> {t('agent.adminAnalyticsRefresh')}
          </button>
        </div>

        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
          </div>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        {data && !loading && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: t('agent.adminEnabled'), value: data.enabled ? 'Yes' : 'No' },
                { label: t('agent.adminTotalSessions'), value: String(data.totalSessions) },
                { label: t('agent.adminLast7Days'), value: String(data.last7Days) },
                { label: t('agent.adminDailyCap'), value: String(data.dailyCapPerUser) },
              ].map((item) => (
                <div key={item.label} className="glass-card rounded-xl p-4">
                  <div className="text-xs text-muted-foreground">{item.label}</div>
                  <div className="text-xl font-bold text-foreground mt-1">{item.value}</div>
                </div>
              ))}
            </div>
            <div className="glass-card rounded-xl p-4 text-sm text-muted-foreground">
              {t('agent.adminTemplateRate')}: {Math.round(data.templateRate * 100)}% ·{' '}
              {t('agent.adminAvgLatency')}: {data.avgLatencyMs}ms
            </div>
            <div className="glass-card rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="p-3">{t('agent.adminAgentType')}</th>
                    <th className="p-3">{t('agent.adminCount')}</th>
                    <th className="p-3">{t('agent.adminTemplateCount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byAgentType.map((row) => (
                    <tr key={row.agentType} className="border-b border-border/50">
                      <td className="p-3 font-mono text-xs">{row.agentType}</td>
                      <td className="p-3">{row.count}</td>
                      <td className="p-3">{row.templateCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminAgentAnalytics
